import { type FsmDefAxiosState } from "@do/shared-types";
import axios from "axios";
import { useLayoutEffect, useRef, useState } from "react";
import { type AnyEventObject } from "xstate";
import { type StepValues } from "../../../../types/global";
import { Loader } from "../loader";

type MessageStepAxiosProps = {
  onSetStepValue: React.Dispatch<React.SetStateAction<StepValues>>;
  options: FsmDefAxiosState["meta"]["options"];
  send: (event: AnyEventObject) => void;
  stateDef: FsmDefAxiosState;
  stateValue: string;
};

export const MessageStepAxios = ({
  onSetStepValue,
  options,
  send,
  stateDef,
  stateValue,
}: MessageStepAxiosProps) => {
  const [loading, setLoading] = useState(false);
  const sendRef = useRef(send);
  const onSetStepValueRef = useRef(onSetStepValue);
  const optionsRef = useRef(options);
  sendRef.current = send;
  onSetStepValueRef.current = onSetStepValue;
  optionsRef.current = options;

  useLayoutEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        setLoading(true);
        const { data } = await axios(optionsRef.current);
        if (cancelled) return;
        setLoading(false);
        onSetStepValueRef.current((currentStepValues: StepValues) => ({
          ...currentStepValues,
          [stateValue]: data,
        }));
        setTimeout(() => sendRef.current({ type: "success" }), 0);
      } catch {
        if (cancelled) return;
        setLoading(false);
        sendRef.current({ type: "error" });
      }
    })();

    return () => {
      cancelled = true;
    };
    // Fire once per axios step. Latest send/onSetStepValue/options are read via refs so
    // identity changes (e.g. after onSetStepValue) do not re-submit the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateDef, stateValue]);

  if (!loading) return null;

  return <Loader />;
};
